/** Employee-profile audit display helpers. Prefer payload display snapshots; client resolve is fallback. */

import { getEmployeeHighlightLabel } from '@/constants/employeeHighlightColors';
import { COMP_AMOUNT_BASIS_OPTIONS } from '@/constants/compensation';

/** @type {Record<string, string>} */
const FIELD_LABELS = {
  manager_employee_id: 'Manager',
  sub_department_id: 'Sub-department',
  team_id: 'Team',
  location_id: 'Primary work site',
  terminal_id: 'Terminal',
  position_id: 'Position',
  grade_id: 'Grade',
  department_id: 'Department',
  employment_type: 'Employment type',
  employment_status: 'Employment status',
  personal_phone: 'Personal phone',
  personal_email: 'Personal email',
  residence_address: 'Residential address',
  highlight_color: 'Highlight color',
  nickname: 'Nickname',
  country: 'Country of residence',
  hire_date: 'Hire date',
  leave_start_date: 'Leave start date',
  end_date: 'End date',
  leave_reason: 'Leave reason',
  birthday: 'Birthday',
  job_description: 'HR note',
  annual_vacation_days: 'Annual vacation days',
  notes: 'Notes',
  change_reason: 'Change reason',
  amount_basis: 'Amount basis',
  c_suite_role: 'C-suite role',
  work_location: 'Work location (text)',
  company_name: 'Company',
  phone: 'Phone',
  email: 'Email',
  name: 'Name',
  is_active: 'Account status'
};

/** Fields treated as noise for "meaningful activity" mode. */
const NOISE_FIELDS = new Set([
  'highlight_color',
  'updated_at',
  'last_seen_at',
  'presence'
]);

const MEANINGFUL_FIELDS = new Set([
  'manager_employee_id',
  'department_id',
  'sub_department_id',
  'team_id',
  'position_id',
  'grade_id',
  'employment_type',
  'employment_status',
  'hire_date',
  'is_active',
  'location_id'
]);

const MEANINGFUL_ENTITY_TYPES = new Set([
  'employee_documents',
  'employee_document',
  'development_plans',
  'development_plan',
  'annual_appraisals',
  'annual_appraisal',
  'employee_emergency_contacts',
  'employee_time_off',
  'time_off'
]);

function tryParseAuditJson(value) {
  if (value == null || value === '') return null;
  const raw = String(value).trim();
  if (!raw.startsWith('{') && !raw.startsWith('[')) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function formatExportPayload(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  const rows = obj.exported_rows ?? obj.exported_row_count;
  if (typeof rows === 'number') {
    return `${rows} row${rows === 1 ? '' : 's'} exported`;
  }
  if (obj.filters && typeof obj.filters === 'object') {
    return 'Export';
  }
  return null;
}

const REQUEST_STATUS_LABELS = {
  pending_supervisor: 'Pending supervisor',
  pending_hr: 'Waiting for a super admin',
  approved: 'Approved',
  declined: 'Declined',
  cancelled: 'Cancelled',
};

function formatAuditDay(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return '';
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function formatTimeOffEntrySummary(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  if (!obj.type && !obj.status && !obj.submitted_via && !obj.start_date) return null;
  const parts = [];
  if (obj.type) parts.push(humanizeSnake(obj.type));
  const start = formatAuditDay(obj.start_date);
  const end = formatAuditDay(obj.end_date);
  if (start && end && end !== start) parts.push(`${start} – ${end}`);
  else if (start) parts.push(start);
  const days = obj.requested_days != null && obj.requested_days !== '' ? obj.requested_days : obj.days;
  if (days != null && days !== '') {
    const count = Number(days);
    parts.push(count === 1 ? '1 working day' : `${days} working days`);
  }
  if (obj.submitted_via === 'telegram') parts.push('Telegram');
  else if (obj.submitted_via === 'web') parts.push('App');
  if (obj.status && REQUEST_STATUS_LABELS[obj.status]) parts.push(REQUEST_STATUS_LABELS[obj.status]);
  return parts.length ? parts.join(' · ') : null;
}

function formatContactListSummary(parsed) {
  if (!Array.isArray(parsed)) return null;
  if (parsed.length === 0) return 'None';
  return parsed
    .slice(0, 3)
    .map((c) => {
      if (!c || typeof c !== 'object') return String(c);
      const parts = [c.name, c.relation, c.phone].filter(Boolean);
      return parts.join(' · ') || 'Contact';
    })
    .join('; ') + (parsed.length > 3 ? ` (+${parsed.length - 3} more)` : '');
}

function formatCountPayload(obj) {
  if (obj && typeof obj === 'object' && typeof obj.count === 'number' && !Array.isArray(obj)) {
    return `${obj.count} item(s)`;
  }
  return null;
}

function humanizeSnake(value) {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * @param {unknown} value
 * @param {{ display?: string|null, fieldName?: string|null, lookup?: Record<string, string>|null }} [opts]
 */
export function formatAuditValue(value, opts = {}) {
  if (opts.display != null && String(opts.display).trim() !== '') {
    const display = String(opts.display).trim();
    if (opts.fieldName === 'highlight_color') return getEmployeeHighlightLabel(display);
    return display;
  }

  if (value == null || value === '') return '—';

  const parsed = tryParseAuditJson(value);
  if (parsed) {
    const contacts = formatContactListSummary(parsed);
    if (contacts) return contacts;
    const timeOff = formatTimeOffEntrySummary(parsed);
    if (timeOff) return timeOff;
    const exported = formatExportPayload(parsed);
    if (exported) return exported;
    const count = formatCountPayload(parsed);
    if (count) return count;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      if (parsed.name || parsed.label || parsed.display) {
        return String(parsed.name || parsed.label || parsed.display);
      }
    }
    const compact = JSON.stringify(parsed);
    if (compact.length > 96) return `${compact.slice(0, 93)}…`;
    return compact;
  }

  const text = String(value);
  const field = opts.fieldName || '';
  const lookup = opts.lookup || null;

  if (REQUEST_STATUS_LABELS[text]) return REQUEST_STATUS_LABELS[text];

  if (lookup && lookup[text]) return lookup[text];

  if (field === 'highlight_color') {
    return getEmployeeHighlightLabel(text);
  }

  if (field === 'amount_basis') {
    const match = COMP_AMOUNT_BASIS_OPTIONS.find((row) => row.value === text);
    if (match) return match.label;
  }

  if (/_id$/.test(field) || field.endsWith('_employee_id')) {
    if (/^\d+$/.test(text)) {
      const entity = field.includes('manager') || field.includes('employee') ? 'Employee' : 'Record';
      return `${entity} #${text}`;
    }
  }

  if (field === 'employment_type') return humanizeSnake(text);

  if (text.length > 120) return `${text.slice(0, 117)}…`;
  return text;
}

export function displayAuditValue(value) {
  return formatAuditValue(value);
}

/**
 * @param {object} entry
 * @returns {string}
 */
export function auditFieldLabel(entry) {
  if (!entry) return '—';

  if (entry.action === 'replace') {
    if (entry.entity_type === 'employee_emergency_contacts') {
      return summarizeReplaceAction(entry, 'Emergency contact');
    }
    if (entry.entity_type === 'employee_children') {
      return summarizeReplaceAction(entry, 'Children');
    }
    if (entry.entity_type === 'employee_compensation_item') {
      return 'Compensation package items updated';
    }
    return `${humanizeSnake(entry.entity_type || 'Record')} updated`;
  }
  if (entry.action === 'user.login') return 'User login';
  if (entry.entity_type === 'employee_document' && entry.field_name === 'metadata') {
    return 'Document details';
  }
  if (entry.entity_type === 'employee_document' && entry.field_name === 'document') {
    return 'Document';
  }
  if (entry.action === 'create' && entry.field_name === 'entry') return 'Attendance record created';
  if (entry.action === 'delete' && entry.field_name === 'entry') return 'Attendance record removed';
  if (entry.entity_type === 'time_off_request' && entry.action === 'submit') return 'Leave request';
  if (entry.entity_type === 'employee_compensation' && entry.field_name === 'notes') {
    return 'Notes (content not stored in audit)';
  }
  if (entry.action === 'soft_delete') return 'Moved to recycle bin';
  if (entry.action === 'restore') return 'Restored from recycle bin';
  if (entry.action === 'purge') return 'Permanently purged';

  const field = entry.field_name;
  if (field && FIELD_LABELS[field]) return FIELD_LABELS[field];
  if (field) return humanizeSnake(field);
  return entry.action ? humanizeSnake(entry.action) : '—';
}

function summarizeReplaceAction(entry, singularLabel) {
  const oldParsed = tryParseAuditJson(entry.old_value);
  const newParsed = tryParseAuditJson(entry.new_value);
  const oldCount = Array.isArray(oldParsed) ? oldParsed.length : null;
  const newCount = Array.isArray(newParsed) ? newParsed.length : null;

  if (oldCount === 0 && newCount != null && newCount > 0) {
    const first = formatContactListSummary(newParsed);
    return first && first !== 'None' ? `${singularLabel} added · ${first}` : `${singularLabel} added`;
  }
  if (newCount === 0 && oldCount != null && oldCount > 0) return `${singularLabel} removed`;
  if (newCount != null && oldCount != null && newCount !== oldCount) {
    return `${singularLabel}s updated`;
  }
  return `${singularLabel}s updated`;
}

export function auditActionLabel(action) {
  const labels = {
    soft_delete: 'Soft delete',
    restore: 'Restore',
    purge: 'Purge',
    update: 'Update',
    replace: 'Replace',
    create: 'Create',
    delete: 'Delete',
    submit: 'Submitted',
    cancel: 'Cancelled',
    resubmit: 'Updated',
    approve_supervisor: 'Supervisor approved',
    decline_supervisor: 'Supervisor declined',
    approve_hr: 'Super admin approved',
    decline_hr: 'Super admin declined',
    'user.login': 'Login',
    'compensation.export': 'Export',
    'attendance.export': 'Export',
    'employee_document.update': 'Update',
    'employee_document.upload': 'Upload',
    'employee_document.archive': 'Archive',
    'employee_document.acknowledge': 'Acknowledge',
  };
  if (labels[action]) return labels[action];
  if (!action) return '—';
  return String(action)
    .replace(/[._]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function auditChangeIsSingleValue(entry) {
  if (!entry) return true;
  if (entry.action === 'user.login') return true;
  const oldEmpty = entry.old_value == null || entry.old_value === '';
  const newEmpty = entry.new_value == null || entry.new_value === '';
  return oldEmpty !== newEmpty || (oldEmpty && newEmpty);
}

export function showAuditChangeAsSingle(entry) {
  if (auditChangeIsSingleValue(entry)) return true;
  return formatAuditChangeOld(entry) === '—' && formatAuditChangeNew(entry) === '—';
}

export function singleAuditChangeText(entry) {
  const oldV = formatAuditChangeOld(entry);
  const newV = formatAuditChangeNew(entry);
  if (oldV === '—' && newV === '—') return '—';
  return oldV !== '—' ? oldV : newV;
}

export function formatAuditChangeOld(entry) {
  return formatAuditValue(entry.old_value, {
    display: entry.old_display,
    fieldName: entry.field_name
  });
}

export function formatAuditChangeNew(entry) {
  return formatAuditValue(entry.new_value, {
    display: entry.new_display,
    fieldName: entry.field_name
  });
}

/**
 * Whether an audit row belongs in "meaningful activity".
 * @param {object} entry
 * @returns {boolean}
 */
export function isMeaningfulAuditEntry(entry) {
  if (!entry) return false;
  const field = entry.field_name || '';
  if (NOISE_FIELDS.has(field)) return false;

  if (MEANINGFUL_FIELDS.has(field)) return true;
  if (MEANINGFUL_ENTITY_TYPES.has(entry.entity_type)) return true;

  if (entry.action === 'replace' && (
    entry.entity_type === 'employee_emergency_contacts' ||
    entry.entity_type === 'employee_children'
  )) {
    return true;
  }

  if (['soft_delete', 'restore', 'purge', 'create', 'delete'].includes(entry.action)) {
    return !NOISE_FIELDS.has(field);
  }

  // Manager / org / employment style updates already covered; skip generic nickname/phone noise for activity mode
  if (['nickname', 'personal_phone', 'personal_email', 'residence_address', 'birthday'].includes(field)) {
    return false;
  }

  return false;
}

/**
 * @param {object[]} entries
 * @param {number} [limit]
 */
export function filterMeaningfulAuditEntries(entries, limit = 0) {
  const list = (entries || []).filter(isMeaningfulAuditEntry);
  if (limit > 0) return list.slice(0, limit);
  return list;
}
